import { request } from './http';
import { config } from './config';
import type { CurrentOrder, Profile, Rate, RegionData, RouteInfo, RoutePoint } from '../domain/types';
import { PaymentType } from '../domain/types';

type CreateOrderEnvelope = { data: { id: string; statusId?: number } };
type OrderEnvelope = { data: CurrentOrder };

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
const GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Coerce to a valid Guid string or ZERO_GUID. The backend DestinationPoint
// binds DistrictId as a non-nullable Guid, so an empty string "" (which the RPC
// can return for district_id) or a malformed Guid would cause an ASP.NET
// model-binding 400 (ValidationProblemDetails). ZERO_GUID binds to Guid.Empty.
function guidOrZero(v: string | null | undefined): string {
  return v && GUID_RE.test(v) ? v : ZERO_GUID;
}

// Coerce to a finite number, defaulting to 0. The backend model has non-nullable
// decimal/double value types (Distance, Lat, Lon, PriceEstimated, …); sending
// null/undefined for any of them triggers a model-binding 400.
function num(v: number | null | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

// Build DestinationPoints in the shape flutter_client sends
// (lib/views/main/cubit/main_cubit_route.dart _buildDestinationPointsWithDistricts).
// The pickup (index 0) carries the resolved districtId/h3Code; intermediate stops
// send placeholders — the optimized backend flow resolves them server-side, same
// contract the MVP already used for the B point.
function buildDestinationPoints(points: RoutePoint[], region: RegionData) {
  return points.map((p, i) => ({
    DistrictId: i === 0 ? guidOrZero(region.districtId) : ZERO_GUID,
    h3Code: i === 0 ? region.h3Code ?? '' : '',
    PointId: i.toString(),
    Lat: num(p.lat),
    Lon: num(p.lon),
    Name: p.address,
    FirstLoad: i === 0,
    SecondaryText: '',
    MainText: p.address,
  }));
}

export type CreateOrderInput = {
  profile: Profile;
  points: RoutePoint[]; // [pickup, ...stops] (1..4), mirrors flutter_client
  rate: Rate;
  route: RouteInfo;
  region: RegionData;
  paymentTypeId?: number;
  discountType?: number;
  comment?: string;
  entrance?: string;
  orderPreferences?: string[]; // selected service ids
  servicesPrice?: number; // sum of selected services, added to the rate price
};

// POST /order/rider/orders — mirrors flutter_client order payload. Price fields
// come from the selected rate plus selected services; backend re-validates.
export async function createOrder(input: CreateOrderInput): Promise<CurrentOrder> {
  const {
    profile,
    points,
    rate,
    route,
    region,
    paymentTypeId = PaymentType.Cash,
    discountType = 0,
    comment = '',
    entrance = '',
    orderPreferences = [],
    servicesPrice = 0,
  } = input;
  const pickup = points[0];
  const compensation = num(rate.compensation);
  const compensationType =
    compensation > 0 ? num(rate.compensationType) || 1 : 0;

  // Final price = discounted base + selected services (flutter_client
  // _recalculateSelectedRatePrice: priceWithAll = baseTariffPrice + servicesPrice).
  const finalPrice = Math.round(num(rate.price) + num(servicesPrice));
  const finalBeforeDiscount = Math.round(
    (num(rate.priceBeforeDiscount) || num(rate.price)) + num(servicesPrice),
  );

  // Pre-flight: the backend binds these as non-nullable Guids. If any is empty
  // or malformed (e.g. profile not loaded, region not resolved), fail with a
  // clear Ukrainian message instead of a bare ASP.NET 400.
  const clientId = guidOrZero(profile.id);
  const cityId = guidOrZero(region.cityId);
  const carClassId = guidOrZero(rate.rate);
  if (clientId === ZERO_GUID) {
    throw new Error('Профіль не завантажено — відкрийте додаток заново.');
  }
  if (cityId === ZERO_GUID) {
    throw new Error('Не вдалося визначити місто для замовлення.');
  }
  if (carClassId === ZERO_GUID) {
    throw new Error('Оберіть тариф перед замовленням.');
  }

  const orderData = {
    country: region.countryCode || config.defaultCountry,
    currency: region.currencyCode || config.defaultCurrency,
    clientId,
    clientFullname: `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim(),
    clientFirstName: profile.firstName ?? '',
    clientLastName: profile.lastName ?? '',
    clientPhoto: profile.photo ? profile.photo : null,
    clientPhone: profile.phone ?? '',
    clientRating: num(profile.rating),
    quoteBonus: 0,
    cityId,
    DestinationPoints: buildDestinationPoints(points, region),
    priceEstimated: finalPrice,
    estimatedPriceBeforeVat: finalPrice,
    carClassId,
    priceRised: 0,
    comment: JSON.stringify({ note: comment, entrance }),
    paymentTypeId,
    distance: num(route.distance),
    duration: num(route.duration),
    lat: num(pickup.lat),
    lon: num(pickup.lon),
    orderPreferences,
    discount: num(rate.discount),
    discountType,
    compensation,
    compensationType,
    priceBeforeDiscount: finalBeforeDiscount,
    vat: 0,
    vatAmount: 0,
    rate: carClassId,
    branchId: config.branchId,
  };

  const res = await request<CreateOrderEnvelope>('/order/rider/orders', {
    method: 'POST',
    body: orderData,
  });
  return {
    id: res.data.id,
    statusId: res.data.statusId ?? 0,
    priceEstimated: finalPrice,
    carClassId: rate.rate,
    paymentTypeId,
  };
}

// GET /order/rider/current — active order on app launch (recovery).
export async function getCurrentOrder(): Promise<CurrentOrder | null> {
  try {
    const res = await request<OrderEnvelope>('/order/rider/current');
    return res.data ?? null;
  } catch {
    return null;
  }
}

// GET /order/rider/{orderId} — polled every config.orderPollMs while active.
export async function getOrder(orderId: string, signal?: AbortSignal): Promise<CurrentOrder> {
  const res = await request<OrderEnvelope>(`/order/rider/${orderId}`, { signal });
  return res.data;
}

// POST /order/rider/{orderId}/reject — cancel by the rider.
export async function cancelOrder(orderId: string): Promise<void> {
  await request<unknown>(`/order/rider/${orderId}/reject`, { method: 'POST' });
}
